import { useEffect, useState } from "react";
import Form from "../../../Components/Form";
import FileInput from "../../../Components/FileInput/FileInput";
import DownloadOrderTemplate from "../../../Components/DownloadOrderTemplate";
import Button from "../../../Components/Button";
import useRequest from "../../../Hooks/useRequest";
import { toast } from "react-toastify";
import { CommonTableUI } from "../../../Tables/TableTemplate/common";
import { ErrorTableReport } from "../../../Interfaces/orderInterfaces";
import SahraConnections from "../../Sahra/SahraConnections";
import Modal from "../../../Components/Modal/Modal";
import { Report } from "../../../Interfaces/TableInterfaces";
import FileBulkOrderTableUI from "../../../Tables/Order/FileBulkOrder/FileBulkOrderTableUI";
import Loading from "../../../Components/Loading/Loading";
import OrderPreviewTable, { isCCOrderPreviewRowSendable } from "../OrderPreviewTable";
import { AxiosError } from "axios";
import { addCCBulkOrder, uploadCCOrderFile } from "src/Requests/ccOrder";
import {
  ICCFileUploadOrderRow,
  ICCFileUploadResp,
} from "src/Interfaces/ccOrder";

enum ModalMode {
  BulkOrderPreview,
  BulkOrderUsers,
  BulkOrderResponseTable,
}

interface IProps {}

const BulkOrder = ({}: IProps) => {
  const [excelFile, setExcelFile] = useState<FormData | null>(null);
  const [bulkOrderRows, setBulkOrderRows] =
    useState<Array<ICCFileUploadOrderRow> | null>(null);
  const [uploadFileErrorTableData, setUploadFileErrorTableData] =
    useState<Report<ErrorTableReport> | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState(ModalMode.BulkOrderPreview);

  const showRequestError = (e: AxiosError, fallback: string) => {
    const status = e.response?.status;
    const data = e.response?.data as
      | { value?: unknown; message?: unknown }
      | undefined;
    const message = data?.value || data?.message;

    if (e.code === "ECONNABORTED" || e.code === "ETIMEDOUT")
      toast.error("زمان انتظار پاسخ تمام شد؛ دوباره تلاش کنید");
    else if (!e.response)
      toast.error("ارتباط با سرور برقرار نشد؛ اتصال را بررسی و دوباره تلاش کنید");
    else if (status === 401)
      toast.error("نشست شما منقضی شده است؛ دوباره وارد شوید");
    else if (status === 403)
      toast.error("دسترسی لازم برای این عملیات را ندارید");
    else if (status && status >= 500)
      toast.error("خطای داخلی سرور؛ دوباره تلاش کنید");
    else
      toast.error(
        typeof message === "string" && message.trim() ? message : fallback,
      );
  };

  function errorHandler(e: AxiosError) {
    const data = e.response?.data as Report<ErrorTableReport> | undefined;
    if (
      e.response?.status === 400 &&
      data &&
      Array.isArray(data.report) &&
      Array.isArray(data.translatedHeaders)
    ) {
      setUploadFileErrorTableData(data);
      setIsModalOpen(true);
      setModalMode(ModalMode.BulkOrderPreview);
      return;
    }

    showRequestError(
      e,
      "دریافت پیش‌نمایش اکسل ناموفق بود؛ فایل را بررسی و دوباره تلاش کنید",
    );
  }

  const addBulkOrderRequest = useRequest(
    () => addCCBulkOrder(bulkOrderRows),
    (e) => {
      showRequestError(e, "ارسال سفارش‌ها ناموفق بود");
      setModalMode(ModalMode.BulkOrderUsers);
    },
  );

  const fileOrderRequest = useRequest(
    () => uploadCCOrderFile(excelFile),
    errorHandler,
  );

  const bulkOrderClickHandler = () => {
    if (!excelFile || fileOrderRequest.loading) return;
    fileOrderRequest.resetResponse();
    setUploadFileErrorTableData(null);
    setBulkOrderRows(null);
    fileOrderRequest.fetchData();
  };

  const getOrderRows = (reports: Array<ICCFileUploadResp>) => {
    setBulkOrderRows(reports.map((report) => report.orderRow).filter(isCCOrderPreviewRowSendable));
  };

  useEffect(() => {
    if (fileOrderRequest.response) {
      setIsModalOpen(true);
      setModalMode(ModalMode.BulkOrderPreview);
      getOrderRows(fileOrderRequest.response.report);
      setUploadFileErrorTableData(null);
    }
  }, [fileOrderRequest.response]);

  const getBulkOrderPreviewTable = () => {
    if (uploadFileErrorTableData)
      return (
        <div className="flex flex-col justify-between items-center h-full">
          <div className="border-2 border-white border-b-red-700 text-red-700 w-full text-center mb-3">
            خطا
          </div>
          <CommonTableUI reports={uploadFileErrorTableData} />
        </div>
      );
    if (fileOrderRequest.response)
      return (
        <div className="flex flex-col justify-between items-center h-full">
          <OrderPreviewTable values={fileOrderRequest.response.report.map((report: any) => ({ ...report.orderRow, accessMessage: report.accessMessage || report.orderRow?.accessMessage }))} />
          <div className="mt-3 w-full">
            <Button
              onClick={() => setModalMode(ModalMode.BulkOrderUsers)}
              text="ارسال"
              variant="contained"
              disabled={!bulkOrderRows?.length}
            />
          </div>
        </div>
      );
    else return <></>;
  };

  const getBulkOrderSahraConnectionsModalUI = () => {
    return (
      <div className="flex flex-col justify-start items-center gap-3">
        <SahraConnections initialSearchParam={{ userIds: getUserIds() }} />
        <Button
          onClick={bulkOrderSahraConnectionsModalBtnClickHandler}
          text="ارسال"
          variant="contained"
          loading={addBulkOrderRequest.loading}
        />
      </div>
    );
  };

  const getFileBulkOrderTableUI = () => {
    if (addBulkOrderRequest.response)
      return <FileBulkOrderTableUI reports={addBulkOrderRequest.response} />;
    if (addBulkOrderRequest.loading)
      return (
        <div className="flex justify-center items-center w-full h-60">
          <Loading className="!text-sky-500" />
        </div>
      );
  };

  const getModalUI = () => {
    switch (modalMode) {
      case ModalMode.BulkOrderPreview:
        return getBulkOrderPreviewTable();

      case ModalMode.BulkOrderUsers:
        return getBulkOrderSahraConnectionsModalUI();

      case ModalMode.BulkOrderResponseTable:
        return getFileBulkOrderTableUI();
    }
  };

  const getUserIds = () => {
    if (!bulkOrderRows) return [];
    const res: number[] = [];

    bulkOrderRows.forEach((row) => {
      res.push(row.accountId);
    });
    return res;
  };

  const bulkOrderSahraConnectionsModalBtnClickHandler = () => {
    addBulkOrderRequest.fetchData();
    setModalMode(ModalMode.BulkOrderResponseTable);
  };

  const modalCloseHandler = () => {
    switch (modalMode) {
      case ModalMode.BulkOrderPreview: {
        setIsModalOpen(false);
        break;
      }
      case ModalMode.BulkOrderUsers: {
        setModalMode(ModalMode.BulkOrderPreview);
        break;
      }
      case ModalMode.BulkOrderResponseTable: {
        setIsModalOpen(false);
        break;
      }

      default:
        break;
    }
  };

  return (
    <Form>
      {isModalOpen ? (
        <Modal modalBoxClassName="!min-w-0 w-[1320px]" onClose={modalCloseHandler} UI={getModalUI()} />
      ) : (
        <></>
      )}
      <div className="p-2 w-full">
        <div className="bold mb-2 text-xs">ارسال دسته ای</div>
        <div className="flex flex-wrap items-center gap-3">
          <FileInput
            fileExtension=".xlsx, .xls, .csv"
            UI={<></>}
            onChange={setExcelFile}
            value={excelFile}
            name="formFile"
            label="انتخاب فایل اکسل"
          />
          <DownloadOrderTemplate kind="covered-call" />
        </div>
        <div className="mt-3 w-full">
          <Button
            text="ارسال"
            onClick={bulkOrderClickHandler}
            variant="contained"
            disabled={!excelFile || fileOrderRequest.loading}
            loading={fileOrderRequest.loading}
          />
        </div>
      </div>
    </Form>
  );
};

export default BulkOrder;
